import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { CATEGORICAL_COLORS } from './categoricalColors';

/*
 * StatusBreakdownBar.tsx
 * Single wide proportional bar broken into colored segments per
 * group, generalized from JobStatusBar.tsx for reuse across domains.
 * Each group carries both a count and a value, the caller picks
 * which one sizes the segments via valueField, a plain count or a
 * currency amount, formatted by valueFormatter.
 */

// A single group returned by a status breakdown endpoint, carrying both its count and its value
export interface StatusBreakdownDataPoint {
    group: string;
    count: number;
    value: number;
}

interface StatusBreakdownBarProps {
    title: string;
    tooltipText?: string;
    titleClassName?: string;
    data: StatusBreakdownDataPoint[];
    groupLabels: Record<string, string>;
    // Explicit color per group, falls back to cycling the categorical palette by index
    groupColors?: Record<string, string>;
    loading?: boolean;
    // Which field of each data point sizes the segments, defaults to count
    valueField?: 'count' | 'value';
    valueFormatter?: (value: number) => string;
    // Called with a segment's group when clicked, navigates to the matching filtered list when set
    onSegmentClick?: (group: string) => void;
}

const StatusBreakdownBar: React.FC<StatusBreakdownBarProps> = ({
    title, tooltipText, titleClassName, data, groupLabels, groupColors, loading, valueField = 'count', valueFormatter, onSegmentClick,
}) => {
    const { t } = useTranslation();

    // Sum the total and only show the groups which actually have a value
    const total = data.reduce((sum, point) => sum + point[valueField], 0);
    const segments = data.filter(point => point[valueField] > 0);
    const formatValue = valueFormatter || ((value: number) => value.toString());

    return (
        <div className='dashboard-card status-breakdown-bar-card'>
            <h6 className={titleClassName}>
                {title}
                {tooltipText && (
                    <span className='tooltip-icon'>
                        <FontAwesomeIcon icon={faCircleQuestion} />
                        <span className='tooltip tooltip-right'>{tooltipText}</span>
                    </span>
                )}
            </h6>
            {loading ? (
                <div className='metric-chart-loading'>
                    <div className='loader' />
                </div>
            ) : total === 0 ? (
                <div className='metric-chart-empty'>{t('general.no_data_available')}</div>
            ) : (
                <>
                    <div className='job-status-bar'>
                        {segments.map((point, index) => (
                            <div
                                key={point.group}
                                className={`job-status-bar-segment tooltip-icon no-drag${onSegmentClick ? ' clickable' : ''}`}
                                style={{
                                    width: `${(point[valueField] / total) * 100}%`,
                                    backgroundColor: groupColors?.[point.group] ?? CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length],
                                }}
                                onClick={onSegmentClick ? () => onSegmentClick(point.group) : undefined}
                            >
                                <span className='tooltip'>{groupLabels[point.group] ?? point.group} ({formatValue(point[valueField])})</span>
                            </div>
                        ))}
                    </div>
                    <div className='job-status-bar-labels'>
                        {segments.map((point, index) => (
                            <div
                                key={point.group}
                                className={`job-status-bar-label${onSegmentClick ? ' no-drag clickable' : ''}`}
                                onClick={onSegmentClick ? () => onSegmentClick(point.group) : undefined}
                            >
                                <span className='dot' style={{ backgroundColor: groupColors?.[point.group] ?? CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length] }} />
                                <span>{groupLabels[point.group] ?? point.group} ({formatValue(point[valueField])})</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default StatusBreakdownBar;
